/**
 * Purpose: Plugin registry for Business Detail modules (decisions D-01/D-11/D-12).
 * Inputs: business (with category and optional enabled_modules JSON list),
 *         module context ({ business, isOwner, data }).
 * Outputs: CATEGORY_CONFIG (per-category defaults + labels), MODULE_REGISTRY
 *          (plugin descriptors), resolveModules(ctx) → renderable descriptors.
 *
 * Adding a future module (events, booking, inventory, coupons…) = append ONE
 * descriptor + component file and include its id in category defaults or a
 * business's enabled_modules. The page shell never changes.
 */
import { lazy } from 'react';

const CatalogueModule = lazy(() => import('../components/businesses/detail/modules/CatalogueModule'));
const ServicesModule = lazy(() => import('../components/businesses/detail/modules/ServicesModule'));
const GalleryModule = lazy(() => import('../components/businesses/detail/modules/GalleryModule'));
const ReviewsModule = lazy(() => import('../components/businesses/detail/modules/ReviewsModule'));
const TeamModule = lazy(() => import('../components/businesses/detail/modules/TeamModule'));
const HotelBridgeModule = lazy(() => import('../components/businesses/detail/modules/HotelBridgeModule'));
const MosqueBridgeModule = lazy(() => import('../components/businesses/detail/modules/MosqueBridgeModule'));
const MarketplaceBridgeModule = lazy(() => import('../components/businesses/detail/modules/MarketplaceBridgeModule'));

const ALL_CORE_MODULES = ['catalogue', 'services', 'team', 'gallery', 'reviews'];

/**
 * Per-category defaults and display labels. Headings are configuration —
 * components never hardcode business types (D-03).
 */
export const CATEGORY_CONFIG = {
  restaurant: { defaults: ALL_CORE_MODULES, labels: { catalogue: 'Menu', team: 'Team' } },
  cafe: { defaults: ALL_CORE_MODULES, labels: { catalogue: 'Menu', team: 'Team' } },
  shop: { defaults: ALL_CORE_MODULES, labels: { catalogue: 'Products', team: 'Staff' } },
  pharmacy: { defaults: ALL_CORE_MODULES, labels: { catalogue: 'Medicines & Products', team: 'Pharmacists' } },
  salon: { defaults: ALL_CORE_MODULES, labels: { catalogue: 'Products', team: 'Stylists' } },
  clinic: { defaults: ALL_CORE_MODULES, labels: { catalogue: 'Products', team: 'Doctors' } },
  school: { defaults: ALL_CORE_MODULES, labels: { catalogue: 'Products', team: 'Faculty' } },
  gym: { defaults: ALL_CORE_MODULES, labels: { catalogue: 'Products', team: 'Trainers' } },
  bank: { defaults: ['services', 'team', 'gallery', 'reviews'], labels: { team: 'Staff' } },
  company: { defaults: ALL_CORE_MODULES, labels: { team: 'Team' } },
  property_dealer: { defaults: ['services', 'team', 'gallery', 'reviews'], labels: { team: 'Agents' } },
  other: { defaults: ALL_CORE_MODULES, labels: { team: 'Team' } },
};

const DEFAULT_CONFIG = CATEGORY_CONFIG.other;

export const getCategoryConfig = (category) => CATEGORY_CONFIG[category] || DEFAULT_CONFIG;

export const getModuleLabel = (business, moduleId, fallback) =>
  getCategoryConfig(business?.category).labels?.[moduleId] || fallback;

/**
 * Module ids the business has switched on: explicit `enabled_modules` JSON
 * list when present (Phase 6 field, D-11), otherwise category defaults.
 * Reading the field now means zero rework when the backend ships it.
 */
export const getEnabledModuleIds = (business) => {
  if (Array.isArray(business?.enabled_modules)) return business.enabled_modules;
  return getCategoryConfig(business?.category).defaults;
};

/**
 * Plugin descriptors (D-12). `isAvailable(ctx)` must be cheap and side-effect
 * free; visitors never see empty modules, owners see them with add-CTAs.
 * ctx = { business, isOwner, data: { products, menuItems, services, galleryImages, reviews } }.
 */
export const MODULE_REGISTRY = [
  {
    id: 'catalogue',
    title: (ctx) => getModuleLabel(ctx.business, 'catalogue', 'Products'),
    isAvailable: (ctx) =>
      ctx.isOwner ||
      (ctx.data.products?.length || 0) > 0 ||
      (ctx.data.menuItems?.length || 0) > 0,
    Component: CatalogueModule,
  },
  {
    id: 'services',
    title: (ctx) => getModuleLabel(ctx.business, 'services', 'Services'),
    isAvailable: (ctx) => ctx.isOwner || (ctx.data.services?.length || 0) > 0,
    Component: ServicesModule,
  },
  {
    id: 'gallery',
    title: () => 'Gallery',
    isAvailable: (ctx) =>
      ((ctx.data.galleryMedia?.length ?? ctx.data.galleryImages?.length) || 0) > 0,
    Component: GalleryModule,
  },
  {
    id: 'team',
    title: (ctx) => getModuleLabel(ctx.business, 'team', 'Team'),
    isAvailable: (ctx) => (ctx.data.teamMembers?.length || 0) > 0,
    Component: TeamModule,
  },
  // Bridges into dedicated verticals (D-06): data-gated via the D-19
  // `business.bridges` contract and alwaysEligible — a linked vertical shows
  // regardless of category defaults, because this page is the identity home.
  {
    id: 'hotel_bridge',
    title: () => 'Rooms & Booking',
    isAvailable: (ctx) => !!ctx.data.bridges?.hotel,
    Component: HotelBridgeModule,
    alwaysEligible: true,
  },
  {
    id: 'mosque_bridge',
    title: () => 'Prayer Times & Community',
    isAvailable: (ctx) => !!ctx.data.bridges?.mosque,
    Component: MosqueBridgeModule,
    alwaysEligible: true,
  },
  {
    id: 'marketplace_bridge',
    title: () => 'Shop Online',
    isAvailable: (ctx) => !!ctx.data.bridges?.marketplace,
    Component: MarketplaceBridgeModule,
    alwaysEligible: true,
  },
  {
    id: 'reviews',
    title: () => 'Ratings & Reviews',
    isAvailable: () => true,
    Component: ReviewsModule,
  },
];

/**
 * Resolve the ordered list of modules to render for this view.
 * enabled flags ⊕ availability (data presence / viewer role).
 * `alwaysEligible` descriptors (bridges) skip the enabled check — they are
 * gated purely by linked data, so the identity page always surfaces them.
 */
export const resolveModules = (ctx) => {
  if (!ctx?.business) return [];
  const enabled = getEnabledModuleIds(ctx.business);
  return MODULE_REGISTRY.filter(
    (m) => (m.alwaysEligible || enabled.includes(m.id)) && m.isAvailable(ctx),
  );
};
